'use client'

import Link from 'next/link'
import { Button } from './ui/Button'
import { AppHeader, HeaderTopGapMask } from './AppHeader'
import { HeaderAuthLink } from './HeaderAuthLink'
import { useAuth } from './AuthProvider'

// Shared header for public marketing pages that are reachable both logged
// out and logged in (features index, blog, the Job Search Copilot feature
// page). A logged-in visitor gets the exact same AppHeader every
// authenticated app page uses — same floating "island" treatment at lg+,
// same colours — rather than a page-specific one; a logged-out visitor gets
// the public nav (Blog/Features/Contact/Get Started), styled to match the
// same dark chrome and floating position so switching between logged-in and
// logged-out states (or between this page and any app page) never jumps.
export function MarketingHeader({
  onMenuClick, active, showMenuButton = true,
}: {
  onMenuClick?: () => void
  active?: 'blog' | 'features' | 'contact'
  // Passed straight through to AppHeader for the logged-in branch — for the
  // rare page (e.g. pricing) that deliberately never shows a Sidebar, so
  // there's nothing for a mobile menu toggle to open.
  showMenuButton?: boolean
}) {
  const { user } = useAuth()

  if (user) {
    return <AppHeader onMenuClick={onMenuClick} showMenuButton={showMenuButton} />
  }

  return (
    <>
      <HeaderTopGapMask />
      <header className="bg-[linear-gradient(135deg,#0284c7_0%,#0369a1_45%,#0c4a6e_100%)] border-b border-primary-700 sticky top-0 z-50 shadow-md lg:top-4 lg:mx-4 lg:rounded-2xl lg:shadow-lg lg:border-b-0">
        <div className="px-4 py-3 sm:py-4 flex justify-between items-center">
          {/* Same lg:-ml-4 + lg:w-56 + lg:justify-center centering as
              AppHeader's logo (see its comment) — kept identical so the
              brand mark sits in the same spot whether or not this
              particular page happens to be showing a Sidebar underneath. */}
          <div className="flex items-center lg:-ml-4 lg:w-56 lg:justify-center">
            <Link href="/" className="text-xl sm:text-3xl font-bold text-white">CVCircuit</Link>
          </div>
          {/* Never wraps: at 360px the four links used to push "Sign in" onto
              a second line and grow the header to 85px. Smaller text below sm
              keeps them on one line down to 360px; under that, Blog (the
              least essential of the four) gives up its slot. */}
          <nav className="flex flex-nowrap whitespace-nowrap gap-3 sm:gap-6 items-center justify-end text-sm sm:text-base">
            <Link href="/blog" className={`max-[359px]:hidden ${active === 'blog' ? 'text-white font-semibold' : 'text-primary-100 hover:text-white transition-colors'}`}>Blog</Link>
            <Link href="/features" className={active === 'features' ? 'text-white font-semibold' : 'text-primary-100 hover:text-white transition-colors'}>Features</Link>
            <Link href="/contact" className={active === 'contact' ? 'text-white font-semibold' : 'text-primary-100 hover:text-white transition-colors'}>Contact</Link>
            <HeaderAuthLink className="text-primary-100 hover:text-white transition-colors" />
            <Link href="/cv/start" className="hidden sm:block">
              <Button variant="white">Get Started Free</Button>
            </Link>
          </nav>
        </div>
      </header>
    </>
  )
}
